/**
 * Property-related TypeScript types
 * Includes both API response types and frontend-adapted types
 */

// =============================================================================
// Common Types
// =============================================================================

/**
 * Localized field from backend - used for enums and reference data
 * Frontend uses `code` for logic, `label` for display
 */
export interface LocalizedField {
  code: string;
  label: string;
}

/**
 * Photo variant with URL and dimensions
 */
export interface PhotoVariant {
  url: string;
  width: number;
  height: number;
}

/**
 * Photo variants for different contexts
 */
export interface PhotoVariants {
  thumb: PhotoVariant | null;   // 200w - thumbnails
  card: PhotoVariant | null;    // 640w - property cards
  gallery: PhotoVariant | null; // 1280w - gallery view
  hero: PhotoVariant | null;    // 1920w - hero sections
}

/**
 * Photo with variants
 */
export interface Photo {
  id: string;
  position: number;
  variants: PhotoVariants;
}

// =============================================================================
// API Response Types (match backend exactly)
// =============================================================================

export type PropertyTypeCode = 'villa' | 'apartment' | 'duplex' | 'townhouse';
export type ListingStatusCode = 'active' | 'sold' | 'rented' | 'draft' | 'pending' | 'deleted';

/**
 * Listing response from API
 */
export interface ApiListing {
  id: string;
  title: string;
  description: string | null;
  address: string;
  short_code: string | null;
  property_type: LocalizedField;
  status: LocalizedField;
  district: LocalizedField | null;
  city: LocalizedField;
  features: LocalizedField[];
  price: number;
  bedrooms: number;
  bathrooms: number;
  area: number;
  year_built: number | null;
  photos: Photo[];
}

/**
 * Paginated listings response from API
 */
export interface ApiListingsResponse {
  items: ApiListing[];
  total: number;
  page: number;
  page_size: number;
  pages: number;
}

/**
 * District response from API
 */
export interface ApiDistrict {
  code: string;
  label: string;
  listing_count?: number | null;
}

/**
 * Districts list response from API
 */
export interface ApiDistrictsResponse {
  districts: ApiDistrict[];
}

/**
 * Cities list response from API
 */
export interface ApiCitiesResponse {
  cities: LocalizedField[];
}

/**
 * Property types list response from API
 */
export interface ApiPropertyTypesResponse {
  property_types: LocalizedField[];
}

// =============================================================================
// Frontend-Adapted Types (used by components)
// =============================================================================

/**
 * Property as used by frontend components
 * Adapted from ApiListing for easier consumption
 */
export interface Property {
  id: string;
  title: string;
  description: string | null;
  address: string;
  shortCode: string | null;
  propertyType: LocalizedField;
  status: LocalizedField;
  district: LocalizedField | null;
  city: LocalizedField;
  features: LocalizedField[];
  price: number;
  bedrooms: number;
  bathrooms: number;
  area: number;
  yearBuilt: number | null;
  photos: Photo[];
}

/**
 * Paginated response for frontend
 */
export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export type PropertiesResponse = PaginatedResponse<Property>;

/**
 * District for frontend use
 */
export interface District {
  code: string;
  label: string;
  listingCount?: number | null;
}

/**
 * Frontend filter state (camelCase)
 */
export interface PropertyFilters {
  search?: string;
  district?: string;
  propertyType?: string;
  city?: string;
  minPrice?: number;
  maxPrice?: number;
  minBedrooms?: number;
  maxBedrooms?: number;
  minArea?: number;
  maxArea?: number;
  status?: ListingStatusCode;
  page?: number;
  pageSize?: number;
}

// =============================================================================
// Constants & Helpers
// =============================================================================

/**
 * Placeholder image for listings without photos
 */
export const PLACEHOLDER_IMAGE = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 300'%3E%3Crect fill='%23f1f5f9' width='400' height='300'/%3E%3Cpath d='M200 100l60 45v75h-45v-45h-30v45h-45v-75z' fill='%2310b981'/%3E%3Ccircle cx='280' cy='80' r='20' fill='%23fbbf24'/%3E%3C/svg%3E";

/**
 * Get the best available photo URL for a given context
 */
export function getPhotoUrl(
  photos: Photo[] | undefined,
  variant: keyof PhotoVariants = 'card'
): string {
  if (!photos || photos.length === 0) {
    return PLACEHOLDER_IMAGE;
  }

  const photo = photos[0];
  const variantData = photo.variants[variant];

  if (variantData?.url) {
    return variantData.url;
  }

  // Fallback chain: card -> gallery -> hero -> thumb
  const fallbackOrder: (keyof PhotoVariants)[] = ['card', 'gallery', 'hero', 'thumb'];
  for (const fallbackVariant of fallbackOrder) {
    const fallback = photo.variants[fallbackVariant];
    if (fallback?.url) {
      return fallback.url;
    }
  }

  return PLACEHOLDER_IMAGE;
}

/**
 * Get all photo URLs for a specific variant (for galleries)
 */
export function getAllPhotoUrls(
  photos: Photo[] | undefined,
  variant: keyof PhotoVariants = 'gallery'
): string[] {
  if (!photos || photos.length === 0) {
    return [PLACEHOLDER_IMAGE];
  }

  return photos
    .sort((a, b) => a.position - b.position)
    .map(photo => photo.variants[variant]?.url || getPhotoUrl([photo], variant))
    .filter(Boolean);
}

// =============================================================================
// Responsive Image Helpers
// =============================================================================

/**
 * Width values for each variant (must match backend)
 */
const VARIANT_WIDTHS: Record<keyof PhotoVariants, number> = {
  thumb: 200,
  card: 640,
  gallery: 1280,
  hero: 1920,
};

/**
 * Responsive image props for a single photo
 */
export interface ResponsiveImageProps {
  src: string;
  srcSet: string;
  sizes: string;
}

/**
 * Preset sizes configurations for common use cases
 */
export const IMAGE_SIZES = {
  // Property card in grid: full width on mobile, ~1/2 on tablet, ~1/3 on desktop
  card: '(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 400px',
  // Gallery: full width, but consider max-height constraint
  gallery: '(max-width: 768px) 100vw, (max-width: 1280px) 100vw, 1280px',
  // Thumbnail: small fixed size
  thumb: '200px',
} as const;

/**
 * Which variants to include in srcSet for each context
 * This prevents over-fetching (e.g., loading 1920w hero for a 400px card)
 */
const CONTEXT_VARIANTS: Record<'card' | 'gallery' | 'thumb', (keyof PhotoVariants)[]> = {
  thumb: ['thumb', 'card'],           // Max 640w - small thumbnails
  card: ['thumb', 'card', 'gallery'], // Max 1280w - cards in grid
  gallery: ['card', 'gallery'],       // Max 1280w - full gallery (no hero needed)
};

/**
 * Get responsive image props (src, srcSet, sizes) for a photo
 *
 * @example
 * // In component:
 * const imgProps = getResponsiveImageProps(photos, 'card');
 * <img {...imgProps} alt="Property" />
 *
 * @example
 * // With custom sizes:
 * const imgProps = getResponsiveImageProps(photos, 'gallery', '100vw');
 */
export function getResponsiveImageProps(
  photos: Photo[] | undefined,
  context: 'card' | 'gallery' | 'thumb' = 'card',
  customSizes?: string
): ResponsiveImageProps {
  const sizes = customSizes || IMAGE_SIZES[context];

  if (!photos || photos.length === 0) {
    return {
      src: PLACEHOLDER_IMAGE,
      srcSet: '',
      sizes,
    };
  }

  const photo = photos[0];
  const { variants } = photo;

  // Build srcSet only from variants appropriate for this context
  const allowedVariants = CONTEXT_VARIANTS[context];
  const srcSetParts: string[] = [];

  for (const variantKey of allowedVariants) {
    const variant = variants[variantKey];
    if (variant?.url) {
      srcSetParts.push(`${variant.url} ${VARIANT_WIDTHS[variantKey]}w`);
    }
  }

  // Determine best src fallback based on context
  const fallbackVariant = context === 'thumb' ? 'thumb' : context === 'card' ? 'card' : 'gallery';
  const src = getPhotoUrl(photos, fallbackVariant);

  return {
    src,
    srcSet: srcSetParts.join(', '),
    sizes,
  };
}

/**
 * Get responsive props for all photos (for galleries with multiple images)
 */
export function getAllResponsiveImageProps(
  photos: Photo[] | undefined,
  context: 'card' | 'gallery' | 'thumb' = 'gallery',
  customSizes?: string
): ResponsiveImageProps[] {
  if (!photos || photos.length === 0) {
    return [{
      src: PLACEHOLDER_IMAGE,
      srcSet: '',
      sizes: customSizes || IMAGE_SIZES[context],
    }];
  }

  return photos
    .sort((a, b) => a.position - b.position)
    .map(photo => getResponsiveImageProps([photo], context, customSizes));
}
